/* ══════════════════════════════════════════════════════════════════════════
 * Attribute selections → Product3DPreview props.
 *
 * A variation's `three_d` mapping (admin, collection item) is a list of
 * bindings: which product attribute feeds which 3D slot, and per value the
 * parameter that slot needs. This module resolves the CURRENT selections
 * against that mapping — the same pure function renders the live preview,
 * builds the snapshot saved on the cart/order line, and reconstructs legacy
 * lines that carry no snapshot.
 *
 * Slots: bodyColor · ink · frontArtwork · backArtwork · chestCenterArtwork · bagArtwork ·
 *        pocketArtwork · text · specialText · flag · letter · backArtworkScale · backName ·
 *        backNumber · backYear · hidePrint
 * Logo placements (2026-11-04): every artwork slot (ARTWORK_SLOTS — one per backend logo
 * placement) resolves the same way and lands in `props.logos[slot] = {url, inverted}`;
 * `frontImage` / `backImage` (+ `…Inverted`) stay as aliases of the two classic slots, so a
 * DK garment's props are exactly what they were plus `logos`. A slot the template lacks
 * (bag / pocket / chest centre until the next model builds) resolves but draws nothing.
 * Jersey back (vogn-1, 2026-10-29): backName / backNumber take a textfield (capitals,
 * 14 characters; '0'–'99'); backYear prints the binding's `text` (admin «Year line text»,
 * e.g. "Student 2027"; `{year}` = the shop's season year, `seasonYear` from the variation
 * payload), else "STUDENT <season year>" — by default whenever the garment has the slot, and
 * a bound attribute decides per value (param `show` | `none`); hidePrint
 * leaves a fixed print off for a value (param = the print's slot, e.g. «Fjern logo» =
 * yes → `chestWordmark`, the STUDENTERUGEN chest print).
 * Sources: tint | logo_front | logo_back | value_image | textfield | auto |
 *          school_logo | school_logo_uploaded | school_logo_imported |
 *          brand_default_logo | class_logo:<key> | class_logo_uploaded:<key> |
 *          brand_default_class_logo
 * Artwork sources for frontArtwork/backArtwork:
 *  - class_logo:<key> / school_logo read the page-level `logoAssets`
 *    ({school, class: {front, back, …}} — the variation payload's `logo_assets`,
 *    resolved per school/shop/class by the API), so ANY attribute (e.g. the
 *    "Lille Brysttryk" select) can show one of the class's uploads. The admin
 *    picks per variation which attribute shows which upload. The artwork is
 *    applied only while the attribute has a selected value (or is display-only).
 *  - school_logo is the AUTOMATIC school logo (API-resolved: uploaded file →
 *    imported "Logo" URL → the brand's default school logo); the three
 *    specific sources read `logoAssets.school_sources` and show that one image
 *    or nothing (no fallback). When the image drawn is a school logo (any of
 *    them), the props carry frontImageInverted / backImageInverted: the preview
 *    then checks the pixels and keys out a dark backdrop (white-on-black logo).
 *  - class_logo:back is automatic as well (API-resolved: the class's back
 *    upload → the brand's "Default class logo (back print)"), also with no class
 *    in context; class_logo_uploaded:<key> and brand_default_class_logo read
 *    `logoAssets.class_sources` ({uploaded: {front, back}, brand_default}) and
 *    show that one image or nothing. The brand default class logo gets the same
 *    light-on-dark check as the school logos.
 *  - logo_front / logo_back use the bound attribute's own logo URLs (logo-type
 *    attributes); value_image the selected value's image.
 * ══════════════════════════════════════════════════════════════════════════ */
import palette from '@ajat/design-system/Product3DPreview/config/palette.json';
import { getThreeDTemplate } from '@ajat/design-system/Product3DPreview/templates';
import { normalizeJerseyNumber } from '@ajat/design-system/Product3DPreview/jerseyNumber';
import { isArtworkSlot } from '@ajat/design-system/Product3DPreview/artworkSlots';
import { aliasesOfLogos, mergeLogoProps } from '@ajat/design-system/Product3DPreview/logosProps';

const INKS = Object.fromEntries((palette.inks || []).map((i) => [i.id, i.hex ?? i.swatch ?? i.value]));
// Per garment (palette.colors.<template.palette>): UI swatch hex → 3D tint
const TINTS_BY_PALETTE = Object.fromEntries(Object.entries(palette.colors || {})
  .filter(([key, list]) => !key.startsWith('_') && Array.isArray(list))
  .map(([key, list]) => [key, Object.fromEntries(list.map((c) => [String(c.swatch).toLowerCase(), c.tint]))]));

/** The Vogntrøje's back year line; {year} = the shop's season year. */
export const BACK_YEAR_FORMAT = 'STUDENT {year}';

const luminance = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
};

/** Palette tint for a colour value: mapped param → the garment's palette match by swatch → the hex itself. */
const tintFor = (param, rawHex, paletteKey) => {
  if (param) return param;
  const hex = String(rawHex || '').toLowerCase();
  return TINTS_BY_PALETTE[paletteKey]?.[hex] || (hex.startsWith('#') ? hex : null);
};

/** 3D artwork source → key of `logoAssets.school_sources` (one specific school logo image). */
const SCHOOL_LOGO_SOURCES = {
  school_logo_uploaded: 'uploaded',
  school_logo_imported: 'imported',
  brand_default_logo: 'brand_default',
};

const firstValueId = (v) => (v && typeof v === 'object' ? v.id : v);

/**
 * @param {string} layout         variation.layout ('photos' | template key)
 * @param {object|null} mapping   variation.three_d ({v, bindings[]})
 * @param {object} state          { attributes, selectedAttributes, colors, selectedColor, hiddenControlSlugs, logoAssets, seasonYear }
 * @returns {null | { template, modelUrl, modelConfig, props, views, blurViews }}
 */
export function buildThreeDProps(layout, mapping, state) {
  const template = getThreeDTemplate(layout);
  if (!template) return null;
  const bindings = Array.isArray(mapping?.bindings) ? mapping.bindings : [];
  const { attributes = [], selectedAttributes = {}, colors = [], selectedColor = null, hiddenControlSlugs = null, logoAssets = null, seasonYear = null } = state || {};
  const slots = template.modelConfig?.slots || {};
  // The year line: a text of its own (binding.text) or the format; `{year}` needs the season year
  const yearLineOf = (text) => {
    const format = String(text || '').trim() || BACK_YEAR_FORMAT;
    if (!format.includes('{year}')) return format;
    return seasonYear ? format.replace('{year}', String(seasonYear)) : null;
  };
  // The year line shows by default on a garment with the slot; a bound attribute decides (backYear below).
  let backYearBound = false;
  const byId = Object.fromEntries(attributes.map((a) => [String(a.id), a]));
  const bySlug = Object.fromEntries(attributes.map((a) => [a.slug, a]));

  const props = {};
  // The logo artwork per slot ({url, inverted}); the aliases are derived after the loop.
  const logos = {};
  // attribute slug → camera preset key (binding.view): the page switches the
  // preview to it when the customer touches that attribute; blurViews
  // (binding.blur_view) plays when a textfield loses focus (see useThreeDCamera).
  const views = {};
  const blurViews = {};
  let bodyHexForInk = null;

  const selectedValueOf = (attr) => {
    if (!attr) return { id: null, value: null, text: null };
    if (attr.type === 'textfield') {
      const t = selectedAttributes[attr.slug];
      return { id: null, value: null, text: typeof t === 'string' ? t.trim() : '' };
    }
    if (attr.type === 'color') {
      // Colour is held as an index into `colors` on the PDP; fall back to the attribute selection.
      const sel = selectedColor != null ? colors[selectedColor] : null;
      const val = sel
        ? (attr.values || []).find((v) => (v.rawValue ?? v.value) === sel.color || v.value === sel.name) || null
        : (attr.values || []).find((v) => String(v.id) === String(firstValueId(selectedAttributes[attr.slug]))) || null;
      return { id: val?.id ?? null, value: val, text: null, hex: sel?.color ?? (val?.rawValue ?? null) };
    }
    const id = firstValueId(selectedAttributes[attr.slug]);
    const val = (attr.values || []).find((v) => String(v.id) === String(id)) || null;
    return { id: val?.id ?? null, value: val, text: null };
  };

  for (const b of bindings) {
    const attr = byId[String(b.attribute_id)] || bySlug[b.attribute_slug];
    if (!attr) {
      // The year line keeps its text when its attribute is taken off the product: no customer choice, the line
      // always prints (2026-10-30: the attribute was removed on PROD and the season year came back)
      if (b.slot === 'backYear') {
        backYearBound = true;
        const line = yearLineOf(b.text);
        if (line) props.backYear = line;
      }
      continue;
    }
    if (b.view) views[attr.slug] = String(b.view);
    if (b.blur_view) blurViews[attr.slug] = String(b.blur_view);
    if (hiddenControlSlugs && hiddenControlSlugs.has && hiddenControlSlugs.has(attr.slug)) continue;
    const sel = selectedValueOf(attr);
    const param = sel.id != null ? (b.param_by_value || {})[String(sel.id)] : undefined;

    switch (b.slot) {
      case 'bodyColor': {
        const hex = sel.hex ?? sel.value?.rawValue ?? null;
        const tint = tintFor(param, hex, template.palette);
        if (tint) { props.bodyColor = tint; bodyHexForInk = hex || tint; }
        break;
      }
      case 'ink': {
        const ink = param && INKS[param] ? INKS[param] : (param && /^#/.test(param) ? param : null);
        if (ink) { props.textColor = ink; props.artworkColor = ink; }
        break;
      }
      case 'text':
        if (sel.text) props.text = sel.text.slice(0, 16);
        break;
      case 'specialText':
        if (sel.text) props.specialText = sel.text.slice(0, 16);
        break;
      case 'frontArtwork':
      case 'backArtwork':
      case 'chestCenterArtwork':
      case 'bagArtwork':
      case 'pocketArtwork': {
        if (!isArtworkSlot(b.slot)) break;
        const key = b.slot;
        const source = String(b.source || '');
        let url = null;
        let pageAsset = false;
        if (source.startsWith('class_logo_uploaded:')) { url = logoAssets?.class_sources?.uploaded?.[source.slice('class_logo_uploaded:'.length)] || null; pageAsset = true; }
        else if (source === 'brand_default_class_logo') { url = logoAssets?.class_sources?.brand_default || null; pageAsset = true; }
        else if (source.startsWith('class_logo:')) { url = logoAssets?.class?.[source.slice('class_logo:'.length)] || null; pageAsset = true; }
        else if (source === 'school_logo') { url = logoAssets?.school || null; pageAsset = true; }
        else if (SCHOOL_LOGO_SOURCES[source]) { url = logoAssets?.school_sources?.[SCHOOL_LOGO_SOURCES[source]] || null; pageAsset = true; }
        else if (source === 'logo_front') url = attr.logo_image_front_url || attr.logo_image_url || null;
        else if (source === 'logo_back') url = attr.logo_image_back_url || attr.logo_image_url || null;
        else if (source === 'value_image') url = sel.value?.image_url || null;
        else url = attr.logo_image_url || sel.value?.image_url || null;
        // The artwork follows the customer's choice: page-level assets (class/
        // school uploads) and logo attributes show only while the attribute has
        // a selected value (e.g. Lille Brysttryk = Ja) — except display-only
        // attributes (e.g. the class-logo block), which are always applied in
        // production and have no control to pick.
        const chosen = sel.id != null || (attr.type === 'textfield' && !!sel.text) || !!attr.display_only;
        if (url && (chosen || (!pageAsset && attr.type !== 'logo'))) {
          // School logos (imported, brand default, uploaded) may be white on black, while
          // class artwork is dark on white: flag every school logo image — whichever source
          // picked it, the logo attribute included — so the preview CHECKS its pixels and
          // keys out a dark backdrop when there is one. A dark-on-white logo is unaffected.
          // The brand's default class logo is brand artwork of the same kind (checked too).
          const schoolLogos = [logoAssets?.school, ...Object.values(logoAssets?.school_sources || {}), logoAssets?.class_sources?.brand_default].filter(Boolean);
          // Several bindings on one slot: the last one wins (as before).
          logos[key] = { url, inverted: schoolLogos.includes(url) };
        }
        break;
      }
      case 'flag': {
        // param: "tryk:<iso>" | "broderi:<presetId>"; finish derives from the prefix.
        if (sel.id == null || !param) break;
        const [kind, code] = String(param).split(':');
        if (!code) break;
        props.flag = { kind, code };
        props.flagFinish = kind === 'broderi' ? 'embroidery' : 'print';
        break;
      }
      case 'letter': {
        // param: "A:gloss" | "B:embroidery"; default = first char of the value label, gloss.
        if (sel.id == null) break;
        const [ch, finish] = String(param || `${String(sel.value?.value || '').trim().charAt(0)}:gloss`).split(':');
        if (/^[A-ZÆØÅ]$/i.test(ch || '')) { props.letter = ch.toUpperCase(); props.letterFinish = finish === 'embroidery' ? 'embroidery' : 'gloss'; }
        break;
      }
      case 'backArtworkScale':
        if (sel.id != null) props.backArtworkScale = param === 'sqrt2' ? Math.SQRT2 : 1;
        break;
      case 'backName':
        if (sel.text) props.backName = sel.text.slice(0, 14);
        break;
      case 'backNumber': {
        const number = normalizeJerseyNumber(sel.text ?? sel.value?.value);
        if (number) props.backNumber = number;
        break;
      }
      case 'backYear': {
        // param per value: 'show' (default) | 'none'; no value chosen = the default
        backYearBound = true;
        const line = yearLineOf(b.text);
        if (line && param !== 'none') props.backYear = line;
        break;
      }
      case 'hidePrint':
        // param per value: the fixed print's slot that value leaves off (none = keep every print)
        if (sel.id != null && param && slots[param]) props.hiddenPrints = [...new Set([...(props.hiddenPrints || []), param])];
        break;
      default:
        break;
    }
  }

  if (!backYearBound && slots.backYear) {
    const line = yearLineOf(null);
    if (line) props.backYear = line;
  }

  // Logos: the map for every artwork slot + the classic aliases (frontImage / backImage).
  if (Object.keys(logos).length) {
    props.logos = Object.fromEntries(Object.keys(logos).sort().map((k) => [k, logos[k]]));
    Object.assign(props, aliasesOfLogos(logos));
  }

  // Ink default: luminance rule on the body colour when no ink binding set it.
  if (!props.textColor) {
    const l = luminance(bodyHexForInk);
    const auto = l == null ? 'white' : (l < (palette.autoInk?.luminanceThreshold ?? 140) ? (palette.autoInk?.dark ?? 'white') : (palette.autoInk?.light ?? 'black'));
    props.textColor = INKS[auto] || '#FFFFFF';
    props.artworkColor = props.textColor;
  }

  return { template: template.key, modelUrl: template.modelUrl, modelConfig: template.modelConfig, props, views, blurViews };
}

/** The snapshot version written today: v2 = v1 + `props.logos` (the aliases are kept, so a v1 reader still works). */
export const SNAPSHOT_VERSION = 2;

/** Canonical, serialisable snapshot for the cart/order line (sorted keys, no blobs). */
export function canonicalThreeD(threeD) {
  if (!threeD) return null;
  const out = {};
  for (const k of Object.keys(threeD.props || {}).sort()) {
    const v = threeD.props[k];
    if (v == null) continue;
    if (typeof v === 'string' && v.startsWith('blob:')) continue;
    if (k === 'logos' && v && typeof v === 'object') {
      // one sorted entry per slot, blob URLs dropped like the aliases
      const logos = {};
      for (const slot of Object.keys(v).sort()) {
        const url = v[slot]?.url;
        if (typeof url !== 'string' || !url || url.startsWith('blob:')) continue;
        logos[slot] = { url, inverted: !!v[slot].inverted };
      }
      if (Object.keys(logos).length) out.logos = logos;
      continue;
    }
    out[k] = v;
  }
  return { v: SNAPSHOT_VERSION, template: threeD.template, props: out };
}

/** A saved snapshot back into renderable shape (null if its template is gone). A v1 snapshot
 *  (aliases only) gets its `logos` map; a v2 one gets its aliases re-derived — both render alike. */
export function threeDFromSnapshot(snapshot) {
  const template = getThreeDTemplate(snapshot?.template);
  if (!template) return null;
  const props = { ...(snapshot.props || {}) };
  const logos = mergeLogoProps(props);
  if (Object.keys(logos).length) {
    props.logos = logos;
    Object.assign(props, aliasesOfLogos(logos));
  }
  return { template: template.key, modelUrl: template.modelUrl, modelConfig: template.modelConfig, props };
}
